package org.zq.astrock_ai.ui.research.gushi.data

import org.zq.astrock_ai.storage.ResearchKvStorage

private val BUILTIN_GROUPS = listOf("核心", "观察", "短线")
private const val UNGROUPED = "未分组"

object WatchlistGroupsStore {
    private const val KEY_MAP = "watchlist.groups.v1"
    private const val KEY_NAMES = "watchlist.groupNames.v1"

    fun loadNames(): List<String> {
        val raw = ResearchKvStorage.get(KEY_NAMES)
        if (raw.isNullOrBlank()) return BUILTIN_GROUPS + UNGROUPED
        val saved = raw.split(',')
            .map { it.trim() }
            .filter { it.isNotBlank() && it != UNGROUPED }
            .distinct()
        // 旧版只存自定义名：自动补回内置三档
        val merged = if (saved.isEmpty()) {
            BUILTIN_GROUPS
        } else if (BUILTIN_GROUPS.none { it in saved }) {
            (BUILTIN_GROUPS + saved).distinct()
        } else {
            saved
        }.take(12)
        return merged + UNGROUPED
    }

    fun saveNames(names: List<String>) {
        val cleaned = names
            .map { it.trim() }
            .filter { it.isNotBlank() && it != UNGROUPED }
            .distinct()
            .take(12)
        ResearchKvStorage.put(KEY_NAMES, cleaned.joinToString(","))
    }

    fun addName(name: String): List<String> {
        val n = name.trim().take(8)
        if (n.isBlank() || n == UNGROUPED) return loadNames()
        val next = (loadNames().filter { it != UNGROUPED } + n).distinct()
        saveNames(next)
        return loadNames()
    }

    fun rename(old: String, newName: String): List<String> {
        if (old == UNGROUPED) return loadNames()
        val n = newName.trim().take(8)
        if (n.isBlank() || n == UNGROUPED) return loadNames()
        val current = loadNames().filter { it != UNGROUPED }.toMutableList()
        val idx = current.indexOf(old)
        if (idx < 0) return loadNames()
        if (n != old && n in current) return loadNames()
        current[idx] = n
        saveNames(current)
        return loadNames()
    }

    fun removeName(name: String): List<String> {
        if (name == UNGROUPED) return loadNames()
        saveNames(loadNames().filter { it != UNGROUPED && it != name })
        return loadNames()
    }

    fun moveName(name: String, delta: Int): List<String> {
        if (name == UNGROUPED || delta == 0) return loadNames()
        val list = loadNames().filter { it != UNGROUPED }.toMutableList()
        val idx = list.indexOf(name)
        if (idx < 0) return loadNames()
        val target = (idx + delta).coerceIn(0, list.lastIndex)
        if (target == idx) return loadNames()
        val item = list.removeAt(idx)
        list.add(target, item)
        saveNames(list)
        return loadNames()
    }

    fun canManage(name: String): Boolean = name != UNGROUPED

    /** 分组改名/删除后，把股票映射同步到新名或未分组。 */
    fun remapGroups(
        groups: Map<String, String>,
        oldName: String,
        newName: String?,
    ): Map<String, String> {
        if (oldName == UNGROUPED) return groups
        return groups.mapValues { (_, g) ->
            when {
                g != oldName -> g
                newName == null || newName == UNGROUPED -> UNGROUPED
                else -> newName
            }
        }
    }

    fun load(): Map<String, String> {
        val raw = ResearchKvStorage.get(KEY_MAP) ?: return emptyMap()
        val names = loadNames().toSet()
        return raw.split(';').mapNotNull { part ->
            val idx = part.indexOf('=')
            if (idx <= 0) return@mapNotNull null
            val code = part.substring(0, idx).filter { it.isDigit() }.takeLast(6)
            val group = part.substring(idx + 1).trim().ifBlank { UNGROUPED }
            if (code.length == 6) code to (if (group in names) group else UNGROUPED) else null
        }.toMap()
    }

    fun save(groups: Map<String, String>) {
        val encoded = groups.entries
            .sortedBy { it.key }
            .joinToString(";") { "${it.key}=${it.value}" }
        ResearchKvStorage.put(KEY_MAP, encoded)
    }

    fun groupOf(groups: Map<String, String>, code: String): String {
        val names = loadNames()
        return groups[code]?.takeIf { it in names } ?: UNGROUPED
    }

    fun cycle(current: String, names: List<String> = loadNames()): String {
        val list = names.ifEmpty { BUILTIN_GROUPS + UNGROUPED }
        val idx = list.indexOf(current).takeIf { it >= 0 } ?: -1
        return list[(idx + 1) % list.size]
    }
}

/** 兼容旧引用 */
val WATCHLIST_GROUP_OPTIONS: List<String>
    get() = WatchlistGroupsStore.loadNames()
